(() => {
  if (customElements.get('auriani-search')) return;
  class AurianiSearch extends HTMLElement {
    connectedCallback() {
      this.lifecycle?.abort();
      this.cancel();
      this.input = this.querySelector('input[name="q"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.status = this.querySelector('[data-search-status]');
      if (!this.input || !this.results || !this.dataset.endpoint) return;
      this.lifecycle = new AbortController();
      this.composing = false;
      this.activeIndex = -1;
      const options = { signal: this.lifecycle.signal };
      this.input.addEventListener('compositionstart', () => { this.composing = true; this.cancel(); }, options);
      this.input.addEventListener('compositionend', () => { this.composing = false; this.schedule(); }, options);
      this.input.addEventListener('input', (event) => {
        if (!event.isComposing && !this.composing) this.schedule();
      }, options);
      this.addEventListener('keydown', (event) => this.onKey(event), options);
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.cancel();
      }, options);
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) this.cancel();
      }, options);
      this.querySelector('form')?.addEventListener('submit', () => this.cancel(), options);
      this.closest('dialog')?.addEventListener('close', () => this.cancel(), options);
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.cancel();
    }

    schedule() {
      this.cancel();
      if (this.input.value.trim().length < 2) return;
      this.timer = setTimeout(() => { this.timer = null; this.search(); }, 180);
    }

    cancel() {
      this.request?.abort();
      clearTimeout(this.timer);
      this.timer = null;
      this.pending = false;
      this.sequence = (this.sequence || 0) + 1;
      this.close();
      this.input?.removeAttribute('aria-busy');
    }

    close() {
      if (!this.results) return;
      this.setActive(-1);
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      if (this.status) this.status.textContent = '';
    }

    optionNodes() {
      return [...(this.results?.querySelectorAll('[role="option"]') || [])];
    }

    setActive(index) {
      const options = this.optionNodes();
      this.activeIndex = index >= 0 && index < options.length ? index : -1;
      options.forEach((option, number) => option.setAttribute('aria-selected', String(number === this.activeIndex)));
      const selected = options[this.activeIndex];
      if (selected) {
        this.input.setAttribute('aria-activedescendant', selected.id);
        selected.scrollIntoView?.({ block: 'nearest' });
      } else this.input?.removeAttribute('aria-activedescendant');
    }

    async search() {
      clearTimeout(this.timer);
      this.timer = null;
      const terms = this.input.value.trim();
      if (terms.length < 2 || this.composing) return;
      const sequence = ++this.sequence;
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      this.pending = true;
      const timeout = setTimeout(() => request.abort(), 8000);
      this.input.setAttribute('aria-busy', 'true');
      if (this.status) this.status.textContent = this.dataset.loading || '';
      try {
        const endpoint = new URL(this.dataset.endpoint, location.origin);
        if (endpoint.origin !== location.origin) throw new Error('Unexpected search origin');
        endpoint.searchParams.set('q', terms);
        endpoint.searchParams.set('resources[type]', 'product');
        endpoint.searchParams.set('resources[limit]', '10');
        endpoint.searchParams.set('section_id', 'predictive-search');
        const response = await fetch(endpoint, { signal: request.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Search unavailable');
        const html = await response.text();
        if (sequence !== this.sequence || terms !== this.input.value.trim() || !this.isConnected) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const content = parsed.querySelector('[data-predictive-content]');
        if (!content) throw new Error('Search section unavailable');
        content.querySelectorAll('script,iframe,object,embed,style,link,meta,form').forEach((node) => node.remove());
        for (const node of content.querySelectorAll('*')) {
          for (const attribute of [...node.attributes]) {
            if (attribute.name.toLowerCase().startsWith('on')) node.removeAttribute(attribute.name);
          }
        }
        for (const link of content.querySelectorAll('a[href]')) {
          const target = new URL(link.getAttribute('href'), location.origin);
          if (target.origin !== location.origin || !['http:', 'https:'].includes(target.protocol)) link.removeAttribute('href');
        }
        this.results.replaceChildren(document.importNode(content, true));
        const listbox = this.results.querySelector('[role="listbox"]');
        if (listbox) {
          listbox.id = `${this.results.id}-listbox`;
          this.input.setAttribute('aria-controls', listbox.id);
          this.optionNodes().forEach((option, index) => { option.id = `${listbox.id}-${index}`; });
        }
        this.setActive(-1);
        this.results.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
        if (this.status) this.status.textContent = content.querySelector('[data-result-status]')?.dataset.resultStatus || '';
      } catch (error) {
        if (sequence !== this.sequence || !this.isConnected) return;
        this.close();
        if (this.status) this.status.textContent = this.dataset.error || '';
      } finally {
        clearTimeout(timeout);
        if (sequence === this.sequence) {
          this.pending = false;
          this.input.removeAttribute('aria-busy');
        }
      }
    }

    onKey(event) {
      if (event.isComposing || this.composing) return;
      if (event.key === 'Escape') {
        if (this.results.hidden && !this.pending && !this.timer) return;
        event.preventDefault();
        event.stopPropagation?.();
        this.cancel();
        this.input.focus();
        return;
      }
      if (event.target !== this.input) return;
      if (this.results.hidden) {
        if (['ArrowDown', 'ArrowUp'].includes(event.key) && this.input.value.trim().length >= 2) {
          event.preventDefault();
          this.search().then(() => {
            if (!this.results.hidden && document.activeElement === this.input) this.setActive(event.key === 'ArrowDown' ? 0 : this.optionNodes().length - 1);
          });
        }
        return;
      }
      const options = this.optionNodes();
      if (event.key === 'Enter' && this.activeIndex >= 0) {
        const link = options[this.activeIndex]?.querySelector('a[href]');
        if (link) { event.preventDefault(); link.click(); this.cancel(); }
        return;
      }
      if (!options.length || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      event.preventDefault();
      const next = event.key === 'ArrowDown' ? (this.activeIndex + 1) % options.length : (this.activeIndex <= 0 ? options.length - 1 : this.activeIndex - 1);
      this.setActive(next);
      this.input.focus();
    }
  }
  customElements.define('auriani-search', AurianiSearch);
})();
